import { Box, Flex, Text } from "@chakra-ui/react";
import { BiChevronDown, BiChevronRight } from "react-icons/bi";
import colors from "core/resources/theme/colors";
import { useState } from "react";
import { Collapse } from "@chakra-ui/react";
import { ExecucaoOrcamentariaItemResponse } from "features/execucao-orcamentaria/typings";
import { formatMoney } from "infra/helpers/number";
import ActionsKebabMenu from "components/ActionsKebabMenu/index";
import { MdEdit } from "react-icons/md";
import { MdDelete } from "react-icons/md";
import { BsPencilSquare } from "react-icons/bs";
import { FiltroPrevisaoOrcamentaria } from "core/features/previsao-orcamentaria/typings";
import { FaCheck } from "react-icons/fa";
import InputMoney from "components/Input/InputMoney";

interface CollapsePlanejamentoProps {
  item: ExecucaoOrcamentariaItemResponse;
  nivel?: number;
  index?: number;
  handleDelete: (id: string) => Promise<void>;
  onEdit: (item: ExecucaoOrcamentariaItemResponse) => void;
  onEditMes?: (valor: number, categoriaId: string) => void;
  filtro: FiltroPrevisaoOrcamentaria;
}

export default function CollapsePlanejamento({
  item,
  nivel = 0,
  index = 0,
  handleDelete,
  onEdit,
  onEditMes,
  filtro,
}: CollapsePlanejamentoProps) {
  const [isEditingMes, setIsEditingMes] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((state) => !state);

  const hasItem = !!item.items.length;
  const valorSaldo = hasItem ? item.saldoItems : item.saldo;
  const valorPrevisto = hasItem ? item.previstoItems : item.previsto;
  const valorRealizado = hasItem ? item.realizadoItems : item.realizado;

  const [valorPrevistoEdit, setValorPrevistoEdit] = useState(
    valorPrevisto ?? 0
  );

  const getColorSaldo = () => {
    if (valorSaldo! > 0) {
      return colors.accentBlue;
    }
    if (valorSaldo! < 0) {
      return colors.red;
    }
    return undefined;
  };

  const getBg = () => {
    if (nivel === 0) {
      return colors.white;
    }
    if (hasItem) {
      return colors.blue100;
    }

    return index % 2 ? colors.white : colors.black75;
  };

  const isFiltroMes = !!filtro?.mes && !filtro.media;

  const isFiltroAnual = !filtro?.mes && !filtro.media;

  const _onEditMes = () => {
    if (isEditingMes) {
      if (valorPrevistoEdit !== valorPrevisto) {
        onEditMes?.(valorPrevistoEdit, item.id);
      }
      setIsEditingMes(false);
    } else {
      setIsEditingMes(true);
    }
  };

  return (
    <Box w="100%">
      <Flex
        w="100%"
        minH="50px"
        cursor="pointer"
        bg={getBg()}
        onClick={toggle}
        py={1}
        pr={4}
        pl={2}
        alignItems="center"
        roundedTop={nivel === 0 ? 6 : 0}
        mb="2px"
      >
        <Flex
          w="100%"
          alignItems="center"
          justify="space-between"
          pl={nivel * 5}
        >
          {item?.items.length ? (
            <Flex alignItems="center" h="40px" w="30px">
              {isOpen ? (
                <BiChevronDown size={25} color={colors.black700} />
              ) : (
                <BiChevronRight size={25} color={colors.black700} />
              )}
            </Flex>
          ) : null}
          <Flex
            w="100%"
            pl={hasItem ? 0 : "30px"}
            pr={!hasItem && nivel !== 0 ? 0 : 14}
          >
            <Flex w="100%" justify="space-between" align="center">
              <Flex flex={1}>
                <Text>{`${item.breadcrumb} ${item.nome}`}</Text>
              </Flex>
              {!isOpen || !hasItem ? (
                <Flex align="center" gap={4}>
                  <Flex
                    w="150px"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap={1}
                  >
                    {isFiltroMes && !hasItem && nivel > 0 ? (
                      <Flex
                        w="36px"
                        h="36px"
                        alignItems="center"
                        justifyContent="center"
                        _hover={{
                          backgroundColor: colors.black200,
                        }}
                        cursor="pointer"
                        color={colors.primary}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          _onEditMes();
                        }}
                      >
                        {isEditingMes ? <FaCheck /> : <BsPencilSquare />}
                      </Flex>
                    ) : null}

                    {isEditingMes ? (
                      <Flex w="150px">
                        <InputMoney
                          value={valorPrevistoEdit}
                          onChange={(v) => setValorPrevistoEdit(v!)}
                        />
                      </Flex>
                    ) : (
                      <Text align="end" fontWeight="medium">
                        {formatMoney(valorPrevisto)}
                      </Text>
                    )}
                  </Flex>
                  <Text w="150px" align="end" fontWeight="medium">
                    {formatMoney(valorRealizado)}
                  </Text>
                  <Flex align="center">
                    <Text
                      w="150px"
                      align="end"
                      fontWeight="medium"
                      color={getColorSaldo()}
                    >
                      {formatMoney(valorSaldo)}
                    </Text>
                  </Flex>

                  {!hasItem && nivel !== 0 ? (
                    <Flex h="fit-content" w={10}>
                      <ActionsKebabMenu
                        indexItem={2}
                        item={item}
                        actions={[
                          {
                            title: "Editar",
                            invisible: !isFiltroAnual,
                            icon: () => <MdEdit />,
                            onClick: (item) => {
                              onEdit(item);
                            },
                          },
                          {
                            title: "Excluir",
                            icon: () => <MdDelete />,

                            onClick: (item) => {
                              handleDelete(item.id);
                            },
                          },
                        ]}
                      />
                    </Flex>
                  ) : null}
                </Flex>
              ) : null}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {hasItem ? (
        <>
          <Collapse in={isOpen}>
            {item?.items.map((item, index) => (
              <CollapsePlanejamento
                filtro={filtro}
                item={item}
                key={item.id}
                nivel={nivel + 1}
                index={index}
                onEdit={onEdit}
                handleDelete={handleDelete}
                onEditMes={onEditMes}
              />
            ))}
          </Collapse>
          {isOpen ? (
            <Flex
              align="center"
              bg={getBg()}
              h="50px"
              py={1}
              pr={"72px"}
              pl={2 + (nivel + 1) * 5}
              roundedBottom={nivel === 0 ? 6 : 0}
              justify="space-between"
              borderBottomWidth={nivel > 0 ? 2 : 0}
            >
              <Text pl="30px" fontWeight="medium">
                Total
              </Text>
              <Flex w="fit-content" gap={4}>
                <Flex w="150px" justifyContent="flex-end">
                  <Text fontWeight={"medium"}>
                    {formatMoney(item.previstoItems)}
                  </Text>
                </Flex>
                <Flex w="150px" justifyContent="flex-end">
                  <Text fontWeight={"medium"}>
                    {formatMoney(item.realizadoItems)}
                  </Text>
                </Flex>
                <Flex w="150px" justifyContent="flex-end">
                  <Text fontWeight={"medium"} color={getColorSaldo()}>
                    {formatMoney(item.saldoItems)}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
